import {
  Box,
  Button,
  ComboboxItem,
  Flex,
  Grid,
  Loader,
  LoadingOverlay,
  TextInput,
  Title,
} from "@mantine/core";
import { DatePickerInput, MonthPickerInput } from "@mantine/dates";
import { useDebouncedState, useDisclosure } from "@mantine/hooks";
import { IconCalendar, IconFileExport, IconSearch } from "@tabler/icons-react";
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
  useMantineReactTable,
} from "mantine-react-table";
import React, { useDeferredValue, useEffect, useState } from "react";
import { repositoryDelivery } from "../../../_base/_const/_constVar";
import _breadcrumb from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import {
  changeSearchValue,
  getValueById,
} from "../../../_base/helper/FunctionHelper";
import { BASE_API_DELIVERY } from "../../../config";
import { _localization } from "../../../config/location";
import { MessageResponse } from "../../../model/MessageResponse";
import { getMasterOrgSelect } from "../../../service/getSelectApi";

type ColumnPinningType = {
  left?: string[];
  right?: string[];
};

const ReportForMonth = () => {
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [data, setData] = useState<any[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [height, setHeight] = useState(0);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });
  const rowSelectionDeferred =
    useDeferredValue<MRT_RowSelectionState>(rowSelection);
  const [selectIds, setSelectIds] = useState<string[]>([]);
  const [isCallFirst, setIsCallFirst] = useState<boolean>(false);
  const [visible, { toggle, open, close }] = useDisclosure(false);

  const [search, setSearch] = useState<any>({
    FromDate: new Date(
      new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime() +
        7 * 60 * 60 * 1000
    ).toISOString(),
    ToDate: new Date(
      new Date(
        new Date().getFullYear(),
        new Date().getMonth() + 1,
        0
      ).getTime() +
        7 * 60 * 60 * 1000
    ).toISOString(),
  });

  const [viewMonthTable, setViewMonthTable] = useState<string>("");

  const [keySearch, setKeySearch] = useDebouncedState<string>("", 300);

  const [dataBranch, setDataBranch] = useState<ComboboxItem[]>([]);

  const columns = React.useMemo<MRT_ColumnDef<any>[]>(() => {
    if (!data || !Array.isArray(data)) return [];

    const branchKeys =
      data.length > 0
        ? Object.keys(data[0].branchValues).filter((key) => key !== "TONG")
        : [];

    const sortedKeys = [...branchKeys].sort((a, b) => {
      const aNum = a.replace(/_PCT$/, "");
      const bNum = b.replace(/_PCT$/, "");

      if (aNum !== bNum) {
        return aNum.localeCompare(bNum, undefined, { numeric: true });
      }

      return a.endsWith("_PCT") ? 1 : -1;
    });

    const changeArrayHeader = sortedKeys.reduce(
      (acc: any, curr: any, index: any, array: any) => {
        if (index % 2 === 0) {
          acc.push([curr, array[index + 1]]);
        }
        return acc;
      },
      []
    );

    let dataHeader: MRT_ColumnDef<any>[] = [];

    if (changeArrayHeader.length > 0) {
      dataHeader = [
        { accessorKey: "metricName", header: "Nội dung" },
        ...changeArrayHeader.map((key: any) => ({
          header:
            getValueById(key[0]?.toString(), dataBranch, "label") &&
            getValueById(key[0]?.toString(), dataBranch, "label").length > 0
              ? `${getValueById(key[0]?.toString(), dataBranch, "label")}`
              : `Chi nhánh ${key[0]}`,
          columns:
            key && Array.isArray(key) ? (
              key?.map((item) => ({
                accessorKey: `branchValues.${item}`,
                header: item?.includes("_PCT")
                  ? "Tỷ lệ (%)"
                  : `Tháng ${
                      new Date(new Date(viewMonthTable)).getMonth() + 1
                    } - ${new Date(new Date(viewMonthTable)).getFullYear()}`,
              }))
            ) : (
              <></>
            ),
        })),
        { accessorKey: "branchValues.TONG", header: "Tổng" },
      ];
    } else {
      dataHeader = [];
    }

    return dataHeader;
  }, [data, dataBranch, viewMonthTable]);

  const fetchData = async () => {
    const monthFD = new Date(new Date(search?.FromDate)).getMonth() + 1;
    const monthTD = new Date(new Date(search?.ToDate)).getMonth() + 1;
    const yearFD = new Date(new Date(search?.FromDate)).getFullYear();
    const yearTD = new Date(new Date(search?.ToDate)).getFullYear();

    if (monthFD !== monthTD || yearFD !== yearTD) {
      NotificationExtension.Fails("Chỉ được phép lọc dữ liệu trong 1 tháng !");
    } else {
      setIsLoading(true);
      setIsRefetching(true);
      setIsError(false);
      try {
        let url = `/api/v1/DefectiveItemsList/get-list-technical-warranty-monthly-report?`;

        const params = new URLSearchParams();
        Object.entries(search).forEach(([key, value]) => {
          if (Array.isArray(value) && value.length > 0) {
            value.forEach((item) => params.append(key, String(item)));
          } else if (typeof value === "string" && value.length > 0) {
            params.append(key, value);
          }
        });

        if (keySearch.length > 0) {
          url += `KeySearch=${keySearch}&`;
        }

        url += `${params.toString()}&`;

        const res = await repositoryDelivery.get<MessageResponse<any>>(url);

        if (res && res.success) {
          const result = res.data;
          setData(result);
          setRowCount(res.totalCount);
          setViewMonthTable(search?.FromDate);
        }
      } catch (error) {
        setData([]);
        setIsError(true);
        console.error(error);
        return;
      } finally {
        setIsLoading(false);
        setIsRefetching(false);
      }
    }
  };

  const handleExportExcel = async () => {
    open();
    try {
      const monthFD = new Date(new Date(search?.FromDate)).getMonth() + 1;
      const monthTD = new Date(new Date(search?.ToDate)).getMonth() + 1;
      const yearFD = new Date(new Date(search?.FromDate)).getFullYear();
      const yearTD = new Date(new Date(search?.ToDate)).getFullYear();

      if (monthFD !== monthTD || yearFD !== yearTD) {
        NotificationExtension.Fails(
          "Chỉ được phép xuất dữ liệu trong 1 tháng !"
        );
      } else {
        let url = `/api/v1/ExportExcel/export-technical-warranty-monthly-report?Skip=${
          pagination.pageIndex * pagination.pageSize
        }&Take=${pagination.pageSize}`;

        const params = new URLSearchParams();
        Object.entries(search).forEach(([key, value]) => {
          if (Array.isArray(value) && value.length > 0) {
            value.forEach((item) => params.append(key, String(item)));
          } else if (typeof value === "string" && value.length > 0) {
            params.append(key, value);
          }
        });

        if (keySearch.length > 0) {
          url += `KeySearch=${keySearch}&`;
        }

        url += `&${params.toString()}`;

        const _url = BASE_API_DELIVERY + url;
        const token = localStorage.getItem("token");

        const response = await fetch(_url, {
          method: "GET",
          headers: {
            "Content-Type": "application/vnd.ms-excel",
            Authorization: `Bearer ${token?.replace(`"`, "").replace(`"`, "")}`,
          },
        });

        if (!response.ok) {
          NotificationExtension.Fails("Export Excel thất bại !");
        } else {
          NotificationExtension.Success("Export Excel thành công !");
          const blob = await response.blob();
          const urlss = window.URL.createObjectURL(blob);
          const _name = new Date().toISOString().slice(0, 10);
          let filename = "bao_cao_thang_KTBH" + _name + ".xlsx";
          const link = document.createElement("a");
          link.href = urlss;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      close();
    }
  };

  const fetchDataBranchSelect = async () => {
    const getData = await getMasterOrgSelect();

    if (getData) {
      setDataBranch(
        getData
          .filter(
            (item) =>
              item.value != null && item.text != null && item.value !== "-1"
          )
          .map((item) => ({
            value: item.att1?.toString(),
            label: item.text?.toString(),
          }))
      );
      setIsCallFirst(true);
    }
  };

  // Lưu ghim cột - Function để lấy cấu hình từ localStorage
  const getColumnPinningStorage = () => {
    try {
      const columnPinningStorage = localStorage.getItem(
        "reportRequestTransferColumnPinning"
      );
      return columnPinningStorage
        ? JSON.parse(columnPinningStorage)
        : { left: ["mrt-row-numbers", "metricName"], right: ["action"] }; // Giá trị mặc định
    } catch (error) {
      console.error("Error parsing column pinning storage:", error);
      return { left: ["mrt-row-numbers", "metricName"], right: ["action"] };
    }
  };

  // State cho columnPinning - khởi tạo từ localStorage
  const [columnPinning, setColumnPinning] = useState<ColumnPinningType>(() =>
    getColumnPinningStorage()
  );

  // Effect để lưu columnPinning vào localStorage mỗi khi có thay đổi
  useEffect(() => {
    try {
      localStorage.setItem(
        "reportRequestTransferColumnPinning",
        JSON.stringify(columnPinning)
      );
    } catch (error) {
      console.error("Error saving column pinning to localStorage:", error);
    }
  }, [columnPinning]);

  useEffect(() => {
    //   startTransition(() => {
    const valuesList = Object.keys(rowSelectionDeferred);
    setSelectIds(valuesList);
    if (valuesList.length < 1) setSelectIds([]);
    else {
      const valuesList = Object.keys(rowSelectionDeferred);
      setSelectIds(valuesList);
    }
    //  });
  }, [rowSelectionDeferred]);

  useEffect(() => {
    fetchDataBranchSelect();
  }, []);

  useEffect(() => {
    if (isCallFirst) {
      fetchData();
    }
  }, [pagination?.pageIndex, pagination?.pageSize, isCallFirst]);

  useEffect(() => {
    const headerHeight = headerRef.current?.offsetHeight || 0;
    const handleResize = () => {
      setHeight(window.innerHeight - (130 + headerHeight));
    };

    handleResize(); // Set initial height
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const table = useMantineReactTable({
    columns,
    data,
    enableColumnResizing: false,
    positionToolbarAlertBanner: "bottom",
    enableRowNumbers: true,
    //add custom action buttons to top-left of top toolbar
    enableTopToolbar: false,
    mantineTopToolbarProps: {
      style: {
        borderBottom: "3px solid rgba(128, 128, 128, 0.5)",
        marginBottom: 5,
      },
    },
    localization: _localization,
    enableRowSelection: false,
    enableBottomToolbar: false,
    getRowId: (row) => row.metricId,
    initialState: {
      showColumnFilters: false,
      columnPinning: {
        ...columnPinning,
      },
      columnVisibility: { id: true, phone: false, fax: false },
      density: "xs",
    },
    mantineTableContainerProps: {
      style: { maxHeight: height, minHeight: height },
    },
    enableStickyHeader: true,
    manualPagination: true,
    onRowSelectionChange: setRowSelection,
    rowCount: rowCount,
    onPaginationChange: setPagination,
    mantineTableBodyCellProps: ({ row }) => ({
      style: {
        fontWeight: "500", //conditional styling
        fontSize: "12.5px",
        padding: "5px 15px",
      },
    }),
    state: {
      isLoading,
      pagination: pagination,
      showAlertBanner: isError,
      showProgressBars: isRefetching,
      showSkeletons: isLoading, //loading for the first time with no data
      rowSelection,
      columnPinning,
    },
    onColumnPinningChange: setColumnPinning,
    mantineToolbarAlertBannerProps: isError
      ? { color: "red", children: "Lỗi tải dữ liệu !" }
      : undefined,
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      rowsPerPageOptions: ["50", "100", "200"],
    },
    paginationDisplayMode: "pages",
    enableColumnPinning: true,
    mantineTableProps: {
      striped: false,
    },
    columnFilterDisplayMode: "popover",
    mantineTableBodyRowProps: ({ row }) => ({
      style: { cursor: "pointer" },
    }),
  });

  return (
    <>
      <div
        ref={headerRef}
        style={{
          width: "100%",
        }}
      >
        <LoadingOverlay
          visible={visible}
          zIndex={1000}
          overlayProps={{ radius: "sm", blur: 5 }}
          loaderProps={{
            children: (
              <Flex direction={"column"} align="center" gap="xs">
                <Loader type="dots" size={35}></Loader>
                {/* <Title size={15}> Đang upload tài liệu...</Title> */}
                <Title size={15}> Đang xử lý....</Title>
              </Flex>
            ),
          }}
        />
        <Flex justify={"space-between"} align={"center"} w={"100%"}>
          <_breadcrumb></_breadcrumb>
          <Button
            leftSection={<IconFileExport size={14} />}
            onClick={() => handleExportExcel()}
          >
            Export Excel
          </Button>
        </Flex>
        <Grid mt={10} w={"100%"}>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <TextInput
              placeholder="Nhập từ khóa"
              defaultValue={keySearch}
              onChange={(e) => setKeySearch(e.currentTarget.value)}
              leftSection={<IconSearch color="#15aabf" />}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Từ ngày"
              leftSection={<IconCalendar color="#15aabf" />}
              value={
                search.FromDate && search.FromDate.length > 0
                  ? new Date(search.FromDate)
                  : null
              }
              maxDate={
                search.ToDate && search.ToDate?.length > 0
                  ? new Date(search.ToDate)
                  : new Date()
              }
              locale="vi"
              valueFormat="DD/MM/YYYY"
              onChange={(e) => {
                changeSearchValue(
                  e
                    ? new Date(
                        new Date(e).getTime() + 7 * 3600 * 1000
                      )?.toISOString()
                    : "",
                  "FromDate",
                  setSearch
                );
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <DatePickerInput
              size="sm"
              placeholder="Đến ngày"
              leftSection={<IconCalendar color="#15aabf" />}
              value={
                search.ToDate && search.ToDate.length > 0
                  ? new Date(search.ToDate)
                  : null
              }
              minDate={
                search.FromDate && search.FromDate?.length > 0
                  ? new Date(search.FromDate)
                  : new Date()
              }
              locale="vi"
              valueFormat="DD/MM/YYYY"
              onChange={(e) => {
                changeSearchValue(
                  e
                    ? new Date(
                        new Date(e).getTime() + 7 * 3600 * 1000
                      )?.toISOString()
                    : "",
                  "ToDate",
                  setSearch
                );
              }}
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, xs: 12, md: 6, lg: 2 }}>
            <Button
              onClick={() => fetchData()}
              leftSection={<IconSearch size={14} />}
            >
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>
      <Box mt={10}>
        <MantineReactTable table={table} />
      </Box>
    </>
  );
};

export default ReportForMonth;
